import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileSpreadsheet, Search, ChevronLeft, ChevronRight, CheckCircle2, XCircle } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("da-DK", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—");
const targetText = (t) => (t ? `${t.label || t.id || ""}`.trim() : "");

export default function AdminAuditLog() {
  const [data, setData] = useState(null);
  const [f, setF] = useState({ event: "", user: "", date_from: "", date_to: "", page: 1 });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "" && v !== null));
    api.get("/admin/audit-log", { params }).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [f]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v, page: 1 }));
  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  const exportExcel = async () => {
    setBusy(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([k, v]) => k !== "page" && v !== ""));
      const r = await api.get("/admin/audit-log/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = "audit-log.xlsx"; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Eksport fejlede"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-audit-log-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="label-caps">Admin</p>
          <h1 className="text-3xl sm:text-4xl font-light tracking-tight mt-1 text-[#1A1919]">Audit-log</h1>
          <p className="text-sm text-stone-500 mt-2">Sikkerhedshændelser: logins, koder, adgangskoder, brugere og delingslinks. Kun læsning – opbevares i 24 måneder.</p>
        </div>
        <Button variant="outline" onClick={exportExcel} disabled={busy} className="rounded-xl border-[#EBE8E3] bg-white" data-testid="audit-export-btn"><FileSpreadsheet className="h-4 w-4 mr-2" /> Eksportér Excel</Button>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" /><Input value={f.user} onChange={(e) => set("user", e.target.value)} placeholder="Søg e-mail / bruger" className="h-9 pl-9 rounded-xl bg-white w-64" data-testid="audit-search" /></div>
        <select value={f.event} onChange={(e) => set("event", e.target.value)} className="h-9 rounded-xl border border-[#EBE8E3] bg-white px-3 text-sm" data-testid="audit-event-filter">
          <option value="">Alle hændelser</option>
          {(data?.events || []).map((ev) => <option key={ev.value} value={ev.value}>{ev.label}</option>)}
        </select>
        <input type="date" value={f.date_from} onChange={(e) => set("date_from", e.target.value)} className="h-9 rounded-xl border border-[#EBE8E3] bg-white px-3 text-sm" data-testid="audit-date-from" />
        <input type="date" value={f.date_to} onChange={(e) => set("date_to", e.target.value)} className="h-9 rounded-xl border border-[#EBE8E3] bg-white px-3 text-sm" data-testid="audit-date-to" />
        <span className="text-xs text-stone-400 ml-auto" data-testid="audit-total">{data ? `${data.total} hændelser` : ""}</span>
      </div>
      <div className="overflow-x-auto border-y border-[#EBE8E3]">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs label-caps text-stone-500 border-b border-[#EBE8E3]">
            <th className="py-2.5 pr-4">Tidspunkt</th><th className="py-2.5 pr-4">Hændelse</th><th className="py-2.5 pr-4">Bruger</th><th className="py-2.5 pr-4">Mål</th><th className="py-2.5 pr-4">IP</th><th className="py-2.5 pr-4">Detaljer</th>
          </tr></thead>
          <tbody>
            {data === null && <tr><td colSpan={6} className="py-6 text-stone-400">Indlæser…</td></tr>}
            {data && data.items.length === 0 && <tr><td colSpan={6} className="py-6 text-stone-400" data-testid="audit-empty">Ingen hændelser.</td></tr>}
            {data?.items.map((it) => (
              <tr key={it.id} className="border-b border-[#EBE8E3] hover:bg-[#FBFAF8] align-top" data-testid={`audit-row-${it.id}`}>
                <td className="py-2 pr-4 text-stone-600 whitespace-nowrap">{fmt(it.ts)}</td>
                <td className="py-2 pr-4 font-medium text-[#1A1919] whitespace-nowrap"><span className="inline-flex items-center gap-1.5">{it.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-[#7E856C]" /> : <XCircle className="h-3.5 w-3.5 text-[#A83232]" />}{it.event_label}</span></td>
                <td className="py-2 pr-4 text-stone-600">{it.actor_email || "—"}</td>
                <td className="py-2 pr-4 text-stone-600 max-w-[260px] truncate" title={targetText(it.target)}>{it.target ? <><span className="text-stone-400 text-xs mr-1">{it.target.type}</span>{targetText(it.target)}</> : "—"}</td>
                <td className="py-2 pr-4 text-stone-500 whitespace-nowrap">{it.ip || "—"}</td>
                <td className="py-2 pr-4 text-xs text-stone-500 font-mono max-w-[360px] truncate" title={JSON.stringify(it.meta)}>{Object.keys(it.meta || {}).length ? JSON.stringify(it.meta) : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-end gap-2 text-sm text-stone-500">
        <Button size="sm" variant="outline" className="rounded-xl border-[#EBE8E3]" disabled={f.page <= 1} onClick={() => setF((p) => ({ ...p, page: p.page - 1 }))} data-testid="audit-prev"><ChevronLeft className="h-4 w-4" /></Button>
        <span data-testid="audit-page">Side {f.page} af {pages}</span>
        <Button size="sm" variant="outline" className="rounded-xl border-[#EBE8E3]" disabled={f.page >= pages} onClick={() => setF((p) => ({ ...p, page: p.page + 1 }))} data-testid="audit-next"><ChevronRight className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}
